import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const kpiData = [
  { value: 76, suffix: '%', label: { de: 'Stress im Frontline-Betrieb', en: 'Frontline Stress' }, caption: { de: 'der Frontline-Mitarbeitenden in Deutschland erleben regelmäßig arbeitsbedingten Stress – KI kann gezielt entlasten.', en: 'of frontline employees in Germany regularly experience work-related stress – AI can provide targeted relief.' }, source: 'Quinyx, State of the Frontline Workforce 2024' },
  { value: 34, suffix: '%', label: { de: 'Produktivitätsplus', en: 'Productivity Boost' }, caption: { de: 'mehr Output pro Arbeitsstunde bei serviceorientierten Teams mit KI-Assistenz', en: 'more output per work hour for service-oriented teams with AI assistance' }, source: 'BCG – AI at Work, 2024' },
  { value: 65, suffix: '%', label: { de: 'Teams offen für KI', en: 'Teams Open to AI' }, caption: { de: 'der Frontline Worker erwarten, dass sich die Qualität ihrer Arbeit durch moderne Technologie spürbar verbessert.', en: 'of frontline workers expect the quality of their work to noticeably improve through modern technology.' }, source: 'SD Worx / Quinyx, State of the Frontline Workforce 2024' },
];

function KpiCard({ kpi, index, lang }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [played, setPlayed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || played) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setPlayed(true);
        const target = kpi.value;
        const duration = 1800;
        const start = performance.now();
        const animate = (now) => {
          const t = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - t, 4);
          setCount(Math.round(eased * target));
          if (t < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.3 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [kpi.value, played]);

  return (
    <div ref={ref} data-testid={`kpi-card-${index}`}
      className="bg-white/5 backdrop-blur-sm border border-white/10 p-6 text-center hover:border-[#befa0a]/50 transition-all duration-300 hover:bg-white/[0.08]">
      <div className="flex items-baseline justify-center gap-1 mb-3">
        <span className="text-white text-4xl md:text-5xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>{count}</span>
        <span className="text-[#befa0a] text-lg font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>{kpi.suffix}</span>
      </div>
      <p className="text-white/70 text-sm leading-relaxed mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>{kpi.caption[lang]}</p>
      <p className="text-white/25 text-[10px] italic" style={{ fontFamily: "'Manrope', sans-serif" }}>{kpi.source}</p>
    </div>
  );
}

export default function HeroSection() {
  const { lang, t } = useLanguage();

  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-center overflow-x-hidden">
      <video data-testid="hero-video" autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover">
        <source src="/assets/hero-video.mp4" type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-black/75" />

      <div className="relative z-10 px-6 max-w-[1400px] mx-auto w-full pt-28 pb-20">
        <div className="max-w-4xl">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            data-testid="hero-badge" className="text-[#befa0a] text-xs md:text-sm uppercase tracking-[0.3em] mb-5" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {t('hero.badge')}
          </motion.p>
          <motion.h1 data-testid="hero-title" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
            className="text-white text-3xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight leading-[1.3] pt-[0.15em] overflow-visible"
            style={{ fontFamily: "'Oswald', sans-serif", clipPath: 'none' }}>
            {t('hero.title')}
          </motion.h1>
          <motion.p data-testid="hero-subtitle" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.5 }}
            className="text-white/80 text-base md:text-lg mt-5 max-w-2xl leading-relaxed font-medium" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {t('hero.sub')}
          </motion.p>
        </div>

        {/* KPI Block - animated counters */}
        <motion.div data-testid="kpi-block" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-12 mb-10">
          <p className="text-[#befa0a] text-xs uppercase tracking-[0.25em] mb-6 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {lang === 'de' ? 'Was KI im Retail leisten kann' : 'What AI can deliver in retail'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {kpiData.map((kpi, i) => (
              <KpiCard key={i} kpi={kpi} index={i} lang={lang} />
            ))}
          </div>
        </motion.div>

        {/* CTAs */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 1 }}
          className="flex flex-col sm:flex-row items-start gap-4">
          <button data-testid="hero-cta1" onClick={() => document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-[#befa0a] text-[#0a0a0a] px-10 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white transition-colors inline-block" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {t('hero.cta1')}
          </button>
          <button data-testid="hero-cta2" onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
            className="border border-white/30 text-white px-10 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white hover:text-[#0a0a0a] transition-colors" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {t('hero.cta2')}
          </button>
        </motion.div>
      </div>

      <motion.button data-testid="scroll-indicator" onClick={() => document.getElementById('intro')?.scrollIntoView({ behavior: 'smooth' })}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 hover:text-[#befa0a] transition-colors cursor-pointer">
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }}><ChevronDown size={28} /></motion.div>
      </motion.button>
    </section>
  );
}
